import React, { useState, useEffect, useCallback } from 'react';
import CustomerPortalLayout from '../../components/customer-portal/CustomerPortalLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Building2, Lock, ShieldCheck, CheckCircle2, AlertTriangle,
  Upload, Plus, Trash2, MapPin, Loader2, Check, RefreshCw, Send, Download
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const REQUIRED_KYC_DOCS = [
  // 1. Statutory & Tax Registrations
  {
    key: 'IEC',
    label: 'IEC',
    title: 'IEC (Import Export Code)',
    category: 'Statutory & Tax Registrations',
    desc: 'Mandatory 10-digit code issued by DGFT for cross-border logistics',
    patterns: ['iec', 'import export code', 'import_export_code', 'ieccopy', 'iec copy'],
    isAgentOnly: false,
  },
  {
    key: 'PAN CARD',
    label: 'PAN CARD',
    title: 'Company PAN Card',
    category: 'Statutory & Tax Registrations',
    desc: 'Permanent Account Number copy of entity',
    patterns: ['pan card', 'pancard', 'pan', 'pan copy'],
    isAgentOnly: false,
  },
  {
    key: 'GST COPY',
    label: 'GST COPY',
    title: 'GST Registration Certificate',
    category: 'Statutory & Tax Registrations',
    desc: 'Form GST REG-06 or official GST registration certificate',
    patterns: ['gst copy', 'gstcopy', 'gst certificate', 'gst', 'gstin'],
    isAgentOnly: false,
  },
  {
    key: 'Copy of PAN Allotment',
    label: 'Copy of PAN Allotment',
    title: 'Copy of PAN Allotment',
    category: 'Statutory & Tax Registrations',
    desc: 'PAN Allotment Letter / NSDL / UTI allotment copy',
    patterns: ['copy of pan allotment', 'pan allotment', 'pan allotment letter', 'pan_allotment', 'pan allotment copy'],
    isAgentOnly: false,
  },

  // 2. Constitutional & Legal Documents
  {
    key: 'Certificate of Incorporation',
    label: 'Certificate of Incorporation',
    title: 'Certificate of Incorporation (COI)',
    category: 'Constitutional & Legal Documents',
    desc: 'MCA Certificate of Incorporation / Partnership Deed / Registration',
    patterns: ['certificate of incorporation', 'coi', 'incorporation certificate', 'registration certificate', 'partnership deed'],
    isAgentOnly: false,
  },
  {
    key: 'Memorandum of Association',
    label: 'Memorandum of Association',
    title: 'Memorandum of Association (MOA)',
    category: 'Constitutional & Legal Documents',
    desc: 'Certified copy of company Memorandum of Association',
    patterns: ['memorandum of association', 'moa'],
    isAgentOnly: false,
  },
  {
    key: 'Articles of Association',
    label: 'Articles of Association',
    title: 'Articles of Association (AOA)',
    category: 'Constitutional & Legal Documents',
    desc: 'Certified copy of company Articles of Association',
    patterns: ['articles of association', 'aoa'],
    isAgentOnly: false,
  },
  {
    key: 'Power of Attorney',
    label: 'Power of Attorney',
    title: 'Power of Attorney (POA)',
    category: 'Constitutional & Legal Documents',
    desc: 'Power of Attorney or Board Resolution in favor of authorized signatory',
    patterns: ['power of attorney', 'poa', 'board resolution', 'authority letter'],
    isAgentOnly: false,
  },

  // 3. Authorizations, Identification & Address Proof
  {
    key: 'CHA Authority Letter',
    label: 'CHA Authority Letter',
    title: 'CHA Authority Letter',
    category: 'Authorizations & Address Proof',
    desc: 'Customs Broker authorization issued on company letterhead',
    patterns: ['cha authority letter', 'cha authority', 'cha letter', 'cha_authority_letter'],
    isAgentOnly: false,
  },
  {
    key: 'KYC Form',
    label: 'KYC Form',
    title: 'KYC Form',
    category: 'Authorizations & Address Proof',
    desc: 'Duly filled, signed & stamped customer onboarding KYC format',
    patterns: ['kyc form', 'kycform', 'kyc'],
    isAgentOnly: false,
  },
  {
    key: 'AADHAR CARD',
    label: 'AADHAR CARD',
    title: 'Aadhar Card',
    category: 'Authorizations & Address Proof',
    desc: 'Identification proof of authorized signatory / director',
    patterns: ['aadhar card', 'aadhaar card', 'aadhar', 'aadhaar', 'adhar card', 'adhar'],
    isAgentOnly: false,
  },
  {
    key: 'Copy of Telephone Bill',
    label: 'Copy of Telephone Bill',
    title: 'Copy of Telephone Bill',
    category: 'Authorizations & Address Proof',
    desc: 'Telephone / Electricity / Utility bill as registered office address proof',
    patterns: ['copy of telephone bill', 'telephone bill', 'phone bill', 'utility bill', 'electricity bill', 'telephone_bill'],
    isAgentOnly: false,
  },
  {
    key: 'IF AGENT AGREEMENT COPY',
    label: 'IF AGENT AGREEMENT COPY',
    title: 'Agent Agreement Copy',
    category: 'Agency Representation',
    desc: 'Executed agency or representation contract agreement',
    patterns: ['agent agreement', 'agent agreement copy', 'if agent agreement copy', 'agreement copy', 'agent_agreement_copy'],
    isAgentOnly: true,
  },
];

export default function CustomerPortalKyc() {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [branches, setBranches] = useState([]);
  const [panNumber, setPanNumber] = useState('');
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [savingBranches, setSavingBranches] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('kyc');

  const fetchKycData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('customer_token');
      const res = await fetch(`${API}/api/client-portal/kyc`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
      if (!res.ok) throw new Error('Failed to load KYC information');
      const json = await res.json();
      setData(json);
      setPanNumber(json.pan_number || '');
      setBranches(json.branches && json.branches.length > 0 ? json.branches : [
        {
          id: `new-${Date.now()}`,
          is_head_office: true,
          branch_nick_name: 'Head Office',
          address_line_1: '',
          address_line_2: '',
          city: '',
          state: '',
          country: 'India',
          pincode: '',
          gst_number: '',
          contact_person: '',
          designation: '',
          contact_numbers: [''],
          emails: ['']
        }
      ]);
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchKycData();
  }, [fetchKycData]);

  const isLocked = data?.kyc_locked && !data?.rekyc_requested;
  const isAgent = (data?.business_roles || []).some(r => String(r).toUpperCase() === 'AGENT');
  const activeRequiredDocs = REQUIRED_KYC_DOCS.filter(d => !d.isAgentOnly || isAgent);

  const getUploadedDoc = (docConfig) => {
    return (data?.kyc_documents || []).find(d => {
      const name = (d.document_name || '').trim().toLowerCase();
      return docConfig.patterns.some(p => name === p || name.includes(p));
    });
  };

  const completedCount = activeRequiredDocs.filter(d => !!getUploadedDoc(d)).length;
  const totalRequired = activeRequiredDocs.length;
  const progressPercent = totalRequired > 0 ? Math.round((completedCount / totalRequired) * 100) : 0;
  const isFullyVerified = totalRequired > 0 && completedCount === totalRequired;

  const handleBranchChange = (index, field, value) => {
    setBranches(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleContactNumberChange = (bIndex, cIndex, val) => {
    setBranches(prev => {
      const updated = [...prev];
      const numbers = [...(updated[bIndex].contact_numbers || [''])];
      numbers[cIndex] = val;
      updated[bIndex].contact_numbers = numbers;
      return updated;
    });
  };

  const handleEmailChange = (bIndex, eIndex, val) => {
    setBranches(prev => {
      const updated = [...prev];
      const emails = [...(updated[bIndex].emails || [''])];
      emails[eIndex] = val;
      updated[bIndex].emails = emails;
      return updated;
    });
  };

  const addBranch = () => {
    setBranches(prev => [
      ...prev,
      {
        id: `new-${Date.now()}`,
        is_head_office: false,
        branch_nick_name: `Branch ${prev.length + 1}`,
        address_line_1: '',
        address_line_2: '',
        city: '',
        state: '',
        country: 'India',
        pincode: '',
        gst_number: '',
        contact_person: '',
        designation: '',
        contact_numbers: [''],
        emails: ['']
      }
    ]);
  };

  const removeBranch = (index) => {
    if (branches.length <= 1) {
      toast({ title: 'Notice', description: 'At least one branch is required.' });
      return;
    }
    setBranches(prev => prev.filter((_, i) => i !== index));
  };

  const saveBranches = async () => {
    if (isLocked) {
      toast({ title: 'Locked', description: 'Branch details are submitted and locked.', variant: 'destructive' });
      return;
    }
    try {
      setSavingBranches(true);
      const token = localStorage.getItem('customer_token');
      const res = await fetch(`${API}/api/client-portal/kyc/branches`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          pan_number: panNumber,
          branches: branches.map(b => ({
            ...b,
            id: String(b.id).startsWith('new-') ? null : b.id
          }))
        })
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to save branch details');
      }
      toast({ title: 'Success', description: 'Branch details saved successfully.' });
      fetchKycData();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setSavingBranches(false);
    }
  };

  const handleUploadDoc = async (docName, file) => {
    if (!file) return;
    if (isLocked) {
      toast({ title: 'Locked', description: 'Documents are locked from changes.', variant: 'destructive' });
      return;
    }

    try {
      setUploadingDoc(docName);
      const token = localStorage.getItem('customer_token');
      const fd = new FormData();
      fd.append('file', file);

      const res = await fetch(`${API}/api/client-portal/kyc/documents?document_name=${encodeURIComponent(docName)}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: fd
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to upload document');
      }

      toast({ title: 'Uploaded', description: `${docName} uploaded successfully.` });
      fetchKycData();
    } catch (err) {
      toast({ title: 'Upload Failed', description: err.message, variant: 'destructive' });
    } finally {
      setUploadingDoc(null);
    }
  };

  const handleSubmitKyc = async () => {
    if (completedCount === 0) {
      toast({
        title: 'No Documents Uploaded',
        description: 'Please upload the applicable KYC documents for your company before submitting.',
        variant: 'destructive'
      });
      return;
    }

    try {
      setSubmitting(true);
      const token = localStorage.getItem('customer_token');
      const res = await fetch(`${API}/api/client-portal/kyc/submit`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to submit KYC form');
      }

      toast({
        title: 'KYC Submitted Successfully',
        description: 'Your documents have been submitted and locked for compliance verification.'
      });
      fetchKycData();
    } catch (err) {
      toast({ title: 'Submission Error', description: err.message, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <CustomerPortalLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-blue-600 animate-spin mr-3" />
          <span className="text-slate-600 text-sm">Loading KYC Compliance details...</span>
        </div>
      </CustomerPortalLayout>
    );
  }

  return (
    <CustomerPortalLayout>
      <div className="space-y-6 max-w-5xl mx-auto py-2">
        {/* Header Banner */}
        <div className="bg-slate-900 text-white rounded-2xl p-6 sm:p-7 shadow-lg">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-800 pb-5">
            <div>
              <div className="text-xs uppercase font-bold text-blue-400 tracking-wider font-manrope">
                Account Verification & Statutory Compliance
              </div>
              <h1 className="text-2xl font-bold font-manrope text-white mt-1">
                Company KYC Documentation
              </h1>
            </div>

            <div>
              {data?.rekyc_requested ? (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-amber-500/20 text-amber-300 border border-amber-500/40">
                  <RefreshCw className="w-4 h-4 animate-spin text-amber-300" />
                  Re-KYC Requested by Compliance
                </span>
              ) : isLocked ? (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
                  <Lock className="w-4 h-4 text-emerald-400" />
                  KYC Submitted & Locked
                </span>
              ) : isFullyVerified ? (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-blue-500/20 text-blue-300 border border-blue-500/40">
                  <CheckCircle2 className="w-4 h-4 text-blue-400" />
                  Ready to Submit
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-amber-500/20 text-amber-300 border border-amber-500/40">
                  <AlertTriangle className="w-4 h-4 text-amber-400" />
                  KYC Pending ({completedCount}/{totalRequired})
                </span>
              )}
            </div>
          </div>

          {/* Locked Company Name Card */}
          <div className="mt-5 bg-slate-800/90 border border-slate-700 rounded-xl p-4 flex items-start gap-3">
            <div className="p-2 bg-blue-500/10 rounded-lg text-blue-400 shrink-0">
              <Lock className="w-4 h-4" />
            </div>
            <div>
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                Customer / Registered Entity Name (Locked)
              </div>
              <div className="text-lg font-bold text-white font-manrope mt-0.5">
                {data?.business_name}
              </div>
              <div className="text-xs text-slate-400 mt-0.5">
                Company legal name is fixed and verified. Below, you can update branch contact details and upload required statutory KYC certificates.
              </div>
            </div>
          </div>
        </div>

        {/* Re-KYC Notice */}
        {data?.rekyc_requested && (
          <div className="p-4 bg-amber-50 border border-amber-300 rounded-xl flex items-start gap-3 shadow-sm">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <h4 className="font-bold text-amber-900 text-sm font-manrope">
                Admin Has Requested Updated KYC Documents
              </h4>
              <p className="text-amber-800 text-xs mt-0.5">
                Remarks: <span className="font-semibold">{data.rekyc_remarks || 'Please re-upload latest KYC copies.'}</span>
              </p>
              <p className="text-amber-700 text-[11px] mt-1">
                You can now re-upload the requested document copies and click "Submit KYC" below.
              </p>
            </div>
          </div>
        )}

        {/* Locked Notice */}
        {isLocked && (
          <div className="p-4 bg-emerald-50 border border-emerald-300 rounded-xl flex items-start gap-3 shadow-sm">
            <Lock className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
            <div>
              <h4 className="font-bold text-emerald-900 text-sm font-manrope">
                KYC Verification in Progress
              </h4>
              <p className="text-emerald-800 text-xs mt-0.5">
                Your documents have been submitted and are locked for compliance audit. Document changes are disabled until an update request (Re-KYC) is triggered by our compliance team.
              </p>
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="flex border-b border-slate-200 gap-4">
          <button
            type="button"
            onClick={() => setActiveTab('kyc')}
            className={`pb-3 text-sm font-semibold transition-all border-b-2 flex items-center gap-2 ${
              activeTab === 'kyc'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            <ShieldCheck className="w-4 h-4" />
            KYC Checklist ({completedCount}/{totalRequired})
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('branches')}
            className={`pb-3 text-sm font-semibold transition-all border-b-2 flex items-center gap-2 ${
              activeTab === 'branches'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            <MapPin className="w-4 h-4" />
            Branch & Contact Locations ({branches.length})
          </button>
        </div>

        {/* TAB 1: KYC Checklist */}
        {activeTab === 'kyc' && (
          <div className="space-y-6">
            <Card className="bg-white rounded-xl p-5 border border-slate-200/80 shadow-sm">
              <div className="flex items-center justify-between text-xs font-semibold text-slate-600 mb-2">
                <span>Attached Documents: {completedCount} applicable documents uploaded</span>
                <span className="text-slate-900 font-bold">{progressPercent}%</span>
              </div>
              <div className="w-full bg-slate-100 h-2.5 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all duration-500 ${isFullyVerified ? 'bg-emerald-500' : 'bg-blue-600'}`}
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </Card>

            <div className="p-3.5 bg-blue-50/80 border border-blue-200 rounded-xl text-xs text-blue-900 flex items-start gap-2.5">
              <CheckCircle2 className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
              <div>
                <span className="font-semibold">Upload whatever applies to your company:</span> Different business types (Pvt Ltd, LLP, Partnership, Proprietorship) require different documents. Upload whichever documents apply to your entity structure. Documents not applicable to you can simply be skipped.
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {activeRequiredDocs.map((docConfig) => {
                const uploaded = getUploadedDoc(docConfig);
                const isUploading = uploadingDoc === docConfig.key;

                return (
                  <Card
                    key={docConfig.key}
                    className={`rounded-xl border p-5 flex flex-col justify-between transition-all ${
                      uploaded
                        ? 'bg-emerald-50/40 border-emerald-300 shadow-sm'
                        : 'bg-white border-slate-200/90 hover:border-blue-400 hover:shadow-sm'
                    }`}
                  >
                    <div>
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="font-bold text-sm text-slate-900 flex items-center gap-2">
                          {uploaded ? (
                            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                          ) : (
                            <FileText className="w-4 h-4 text-blue-500 shrink-0" />
                          )}
                          {docConfig.title}
                        </div>
                        {uploaded ? (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 uppercase">
                            ✓ Attached
                          </span>
                        ) : (
                          <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200 uppercase">
                            As Applicable
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 mb-4 leading-relaxed">
                        {docConfig.desc}
                      </p>
                    </div>

                    <div className="pt-3 border-t border-slate-100 flex items-center justify-between">
                      {uploaded ? (
                        <>
                          <div className="truncate max-w-[200px] text-xs text-slate-700 font-medium">
                            {uploaded.file_name}
                          </div>
                          <div className="flex items-center gap-2">
                            {!isLocked && (
                              <div>
                                <input
                                  type="file"
                                  id={`portal-replace-${docConfig.key}`}
                                  className="hidden"
                                  onChange={(e) => {
                                    if (e.target.files?.[0]) {
                                      handleUploadDoc(docConfig.key, e.target.files[0]);
                                      e.target.value = '';
                                    }
                                  }}
                                  disabled={isUploading}
                                />
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  disabled={isUploading}
                                  onClick={() => {
                                    const input = document.getElementById(`portal-replace-${docConfig.key}`);
                                    if (input) input.click();
                                  }}
                                  className="h-8 text-xs text-blue-600 border-blue-200 hover:bg-blue-50"
                                >
                                  {isUploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Re-upload'}
                                </Button>
                              </div>
                            )}
                            {isLocked && (
                              <span className="text-[11px] text-slate-400 flex items-center gap-1">
                                <Lock className="w-3 h-3 text-slate-400" /> Locked
                              </span>
                            )}
                          </div>
                        </>
                      ) : (
                        <div className="w-full">
                          <input
                            type="file"
                            id={`portal-upload-${docConfig.key}`}
                            className="hidden"
                            onChange={(e) => {
                              if (e.target.files?.[0]) {
                                handleUploadDoc(docConfig.key, e.target.files[0]);
                                e.target.value = '';
                              }
                            }}
                            disabled={isLocked || isUploading}
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={isLocked || isUploading}
                            onClick={() => {
                              const input = document.getElementById(`portal-upload-${docConfig.key}`);
                              if (input) input.click();
                            }}
                            className="w-full text-xs font-semibold bg-amber-50 hover:bg-amber-100 text-amber-900 border-amber-300 h-9"
                          >
                            {isUploading ? (
                              <>
                                <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
                                Uploading {docConfig.label}...
                              </>
                            ) : (
                              <>
                                <Upload className="w-3.5 h-3.5 mr-2" />
                                Upload {docConfig.label}
                              </>
                            )}
                          </Button>
                        </div>
                      )}
                    </div>
                  </Card>
                );
              })}
            </div>

            {/* Submission Card */}
            <div className="bg-white rounded-xl p-6 border border-slate-200/80 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h4 className="font-bold text-slate-900 font-manrope text-base">
                  Ready to Submit KYC for Compliance Approval?
                </h4>
                <p className="text-xs text-slate-500 mt-0.5">
                  Submitting will lock the documents for formal verification by Stavan Logistics compliance officers.
                </p>
              </div>

              <Button
                type="button"
                onClick={handleSubmitKyc}
                disabled={isLocked || completedCount === 0 || submitting}
                className="bg-accent-green hover:bg-accent-green/90 text-white font-semibold font-ibm px-6 h-11 rounded-lg shrink-0"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : isLocked ? (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    KYC Submitted & Locked
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Submit KYC for Verification
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* TAB 2: Branches */}
        {activeTab === 'branches' && (
          <div className="space-y-6">
            {branches.map((branch, bIdx) => (
              <Card key={branch.id || bIdx} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="bg-slate-50 px-6 py-3.5 border-b border-slate-200 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-slate-900 text-sm">
                      {branch.is_head_office ? 'Head Office Branch' : `Branch #${bIdx + 1}`}
                    </span>
                    {branch.is_head_office && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 uppercase">
                        Primary
                      </span>
                    )}
                  </div>
                  {!isLocked && branches.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeBranch(bIdx)}
                      className="text-red-600 hover:bg-red-50 h-8 px-2 text-xs"
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1" /> Remove
                    </Button>
                  )}
                </div>

                <div className="p-6 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-xs text-slate-700 font-medium">Branch Nickname</Label>
                      <Input
                        value={branch.branch_nick_name}
                        onChange={(e) => handleBranchChange(bIdx, 'branch_nick_name', e.target.value)}
                        disabled={isLocked}
                        placeholder="e.g. Ahmedabad HQ"
                        className="mt-1 h-9 text-xs"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-700 font-medium">GSTIN (Branch GST)</Label>
                      <Input
                        value={branch.gst_number || ''}
                        onChange={(e) => handleBranchChange(bIdx, 'gst_number', e.target.value.toUpperCase())}
                        disabled={isLocked}
                        placeholder="e.g. 24ADXFS3875R1ZR"
                        className="mt-1 h-9 text-xs font-mono"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-700 font-medium">City</Label>
                      <Input
                        value={branch.city || ''}
                        onChange={(e) => handleBranchChange(bIdx, 'city', e.target.value)}
                        disabled={isLocked}
                        placeholder="e.g. Ahmedabad"
                        className="mt-1 h-9 text-xs"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="md:col-span-2">
                      <Label className="text-xs text-slate-700 font-medium">Address Line 1</Label>
                      <Input
                        value={branch.address_line_1 || ''}
                        onChange={(e) => handleBranchChange(bIdx, 'address_line_1', e.target.value)}
                        disabled={isLocked}
                        placeholder="Building, Road, Landmark"
                        className="mt-1 h-9 text-xs"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-700 font-medium">Address Line 2 (Optional)</Label>
                      <Input
                        value={branch.address_line_2 || ''}
                        onChange={(e) => handleBranchChange(bIdx, 'address_line_2', e.target.value)}
                        disabled={isLocked}
                        className="mt-1 h-9 text-xs"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-xs text-slate-700 font-medium">State</Label>
                      <Input
                        value={branch.state || ''}
                        onChange={(e) => handleBranchChange(bIdx, 'state', e.target.value)}
                        disabled={isLocked}
                        className="mt-1 h-9 text-xs"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-700 font-medium">PIN Code</Label>
                      <Input
                        value={branch.pincode || ''}
                        onChange={(e) => handleBranchChange(bIdx, 'pincode', e.target.value)}
                        disabled={isLocked}
                        className="mt-1 h-9 text-xs font-mono"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-700 font-medium">Country</Label>
                      <Input
                        value={branch.country || 'India'}
                        onChange={(e) => handleBranchChange(bIdx, 'country', e.target.value)}
                        disabled={isLocked}
                        className="mt-1 h-9 text-xs"
                      />
                    </div>
                  </div>

                  <div className="pt-4 border-t border-slate-100">
                    <h5 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">
                      Branch Contact Person
                    </h5>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <Label className="text-xs text-slate-700 font-medium">Contact Person</Label>
                        <Input
                          value={branch.contact_person || ''}
                          onChange={(e) => handleBranchChange(bIdx, 'contact_person', e.target.value)}
                          disabled={isLocked}
                          placeholder="Contact Name"
                          className="mt-1 h-9 text-xs"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-700 font-medium">Designation</Label>
                        <Input
                          value={branch.designation || ''}
                          onChange={(e) => handleBranchChange(bIdx, 'designation', e.target.value)}
                          disabled={isLocked}
                          placeholder="Designation"
                          className="mt-1 h-9 text-xs"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-700 font-medium">Mobile Number</Label>
                        <Input
                          value={(branch.contact_numbers && branch.contact_numbers[0]) || ''}
                          onChange={(e) => handleContactNumberChange(bIdx, 0, e.target.value)}
                          disabled={isLocked}
                          placeholder="+91 Phone"
                          className="mt-1 h-9 text-xs"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-700 font-medium">Official Email</Label>
                        <Input
                          value={(branch.emails && branch.emails[0]) || ''}
                          onChange={(e) => handleEmailChange(bIdx, 0, e.target.value)}
                          disabled={isLocked}
                          placeholder="email@company.com"
                          className="mt-1 h-9 text-xs"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            ))}

            {!isLocked && (
              <div className="flex items-center justify-between pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={addBranch}
                  className="text-xs font-semibold text-slate-700 h-10 border-slate-300"
                >
                  <Plus className="w-4 h-4 mr-1.5" /> Add Another Branch Location
                </Button>

                <Button
                  type="button"
                  onClick={saveBranches}
                  disabled={savingBranches}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs h-10 px-6 rounded-lg"
                >
                  {savingBranches ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : 'Save Branch Details'}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </CustomerPortalLayout>
  );
}
